import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys } from "@/api/queryClient";
import type { CaseStatusValue, CaseView } from "@/types";

// #301 (UC-5b): Lebenslauf eines Laufs. Das Objekt-Urteil (Inhaber/Admin) fällt der Master —
// fremde Läufe antworten generisch 404, die UI kennt kein zweites Regelwerk.

function useClient() {
  const { session, logout } = useAuth();
  return () => createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
}

// Auch die INSTANZ-Sicht hängt daran: ein abgebrochener Lauf gibt sie zum Löschen frei, und
// der Delete-Dialog zählt die erhaltenen Läufe. Ohne diese zweite Invalidierung zeigte er
// nach einem Abbruch weiter den alten Stand.
function invalidate(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: queryKeys.resource("cases") });
  void qc.invalidateQueries({ queryKey: queryKeys.resource("process-instances") });
}

function useCaseAction(pfad: string): UseMutationResult<CaseView, Error, string> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: (id) => getClient().post<CaseView>(`/cases/${id}/${pfad}`, {}),
    onSuccess: () => invalidate(qc),
  });
}

/**
 * Abbrechen — der einzige Ausweg aus `blocked`. Ein blockierter Lauf zählt nicht als
 * abgeschlossen und sperrt damit das Löschen seiner Instanz; bis UC-5b gab es dafür keinen
 * Knopf, nur den Griff in die Datenbank.
 */
export function useAbortCase(): UseMutationResult<CaseView, Error, { id: string; reason?: string }> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: ({ id, reason }) =>
      getClient().post<CaseView>(`/cases/${id}/abort`, reason ? { reason } : {}),
    onSuccess: () => invalidate(qc),
  });
}

export function usePauseCase(): UseMutationResult<CaseView, Error, string> {
  return useCaseAction("pause");
}

export function useResumeCase(): UseMutationResult<CaseView, Error, string> {
  return useCaseAction("resume");
}

/**
 * #301 (UC-5c): erneut versuchen. Der Gegenpart zu „Abbrechen" — wer die Ursache einer
 * Blockade behoben hat, konnte den Lauf vorher nur noch beenden.
 */
export function useRetryCase(): UseMutationResult<CaseView, Error, string> {
  return useCaseAction("retry");
}

/**
 * #301 (UC-5d): die offene Rückfrage beantworten.
 *
 * `invalidate` trifft Detail, Verlauf und Liste in einem Zug — der Schlüssel
 * `["resources","cases"]` ist das Präfix aller drei. Nach dem Absenden muss beides frisch
 * sein: die Karte oben verschwindet, und im Verlauf steht die Antwort als eigenes Ereignis.
 */
export function useAnswerCase(): UseMutationResult<unknown, Error, { id: string; answer: string }> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: ({ id, answer }) => getClient().post<unknown>(`/cases/${id}/answer`, { answer }),
    onSuccess: () => invalidate(qc),
  });
}

/**
 * #302 (UC-3): den Lauf erneut anstossen. Der Gegenpart zu „Erneut versuchen", aber an einem
 * anderen Zustand: `retry` holt einen BLOCKIERTEN Lauf zurück, `redispatch` stellt den
 * laufenden Schritt noch einmal zu (der Agent hat den Auftrag verloren/nie gesehen).
 */
export function useRedispatchCase(): UseMutationResult<unknown, Error, string> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: (id) => getClient().post<unknown>(`/cases/${id}/redispatch`, {}),
    onSuccess: () => invalidate(qc),
  });
}

/**
 * #P001 (UC-5): das Modell eines LAUFENDEN Vorgangs wechseln — wirksam ab dem nächsten Schritt.
 *
 * Die Antwort ist die Lauf-Sicht PLUS `warning`: „provider-change" heisst, der Wechsel überquert
 * eine Anbietergrenze (Anthropic ↔ Gateway-Slot oder zwei Slots bei verschiedenen Anbietern) —
 * erlaubt, aber ungetestet. Den Satz dazu schreibt die Oberfläche, der Master beurteilt nur den
 * Fall (deshalb ein Enum am Wire, kein Fliesstext).
 *
 * `warning` ist optional getippt: ein älterer Master liefert das Feld nicht, und `undefined` soll
 * dann nicht als „Warnung" gelesen werden.
 */
export function useSetCaseModel(): UseMutationResult<
  CaseView & { warning?: "provider-change" | null },
  Error,
  { id: string; model: string }
> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: ({ id, model }) =>
      getClient().post<CaseView & { warning?: "provider-change" | null }>(`/cases/${id}/model`, { model }),
    onSuccess: () => invalidate(qc),
  });
}

/**
 * #302 (UC-1): die Freigabe-Entscheidung.
 *
 * ⚠ Der Rückgabewert ist bewusst NICHT die volle Lauf-Sicht (Master-Entscheidung): ein
 * Entscheider ist typischerweise nicht der Inhaber. Er sagt nur, was die eigene Stimme
 * bewirkt hat — `final: false` heisst „Zwischenstand bei Quorum > 1".
 */
export interface CaseDecisionResult {
  caseId: string;
  stepId: string;
  /** #323: `revise` = „zur Überarbeitung zurück" — immer final, wie eine Ablehnung. */
  decision: "approve" | "reject" | "revise";
  final: boolean;
  quorum: number;
  zustimmungen: number;
  /**
   * #323/#324: die Runde, in der diese Entscheidung gefallen ist (1-basiert). Optional
   * getippt: ein Master ohne #323 liefert sie nicht — dann bleibt die Meldung ohne Runde,
   * statt „Runde undefined" zu behaupten.
   */
  runde?: number;
  status: CaseStatusValue;
}

export interface DecideCaseInput {
  id: string;
  stepId: string;
  decision: "approve" | "reject" | "revise";
  /** Nur beim `approve` und nur, wenn der Schritt `produces` deklariert. */
  fields?: Record<string, string | number | boolean>;
  /**
   * #323/#324: der Auftrag an den Ziel-Schritt bzw. die Begründung.
   *
   * Beim `revise` PFLICHT (der Master antwortet sonst 400) — die Oberfläche erzwingt ihn
   * schon vor dem Senden. Beim `approve` VERBOTEN (ebenfalls 400): eine Zustimmung braucht
   * keine Begründung, und der Master weist sie ausdrücklich zurück.
   */
  feedback?: string;
}

export function useDecideCase(): UseMutationResult<CaseDecisionResult, Error, DecideCaseInput> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: ({ id, stepId, decision, fields, feedback }) =>
      getClient().post<CaseDecisionResult>(`/cases/${id}/decide`, {
        stepId,
        decision,
        // Eine Ablehnung ist keine Datenabgabe — der Master lehnt `fields` dort ab.
        ...(decision === "approve" && fields && Object.keys(fields).length > 0 ? { fields } : {}),
        // ⚠ Beim `approve` darf `feedback` NICHT mitgehen (Master: 400). Getrimmt, weil
        // Leerzeichen für den Master kein Text sind — er trimmt selbst und lehnte leeres
        // `feedback` beim `revise` ab; so ist die Nutzlast das, was hier zu sehen war.
        ...(decision !== "approve" && feedback && feedback.trim() !== "" ? { feedback: feedback.trim() } : {}),
      }),
    onSuccess: () => invalidate(qc),
  });
}
